import { z } from 'zod';
import { uploadedFileSchema } from '@terra/shared-authorization';
import {
  BusinessRiskSchema,
  InfraImpactSchema,
} from '@terra/shared-schemas-orchestrator';

export const VriScannerSourceSchema = z.enum([
  'TENABLE',
  'QUALYS',
  'RAPID7',
  'GENERIC_CSV',
]);

export const VriClassificationBucketSchema = z.enum([
  'CONFIRMED_EXPLOITABLE',
  'EXPLOITABLE_WITH_CONTEXT',
  'NOT_EXPLOITABLE',
  'ASSET_NOT_TESTED',
  'ASSET_TESTED_BUT_NOT_IN_SCANNER',
]);

export const VriScanBatchStatusSchema = z.enum([
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
]);

export const PasswordAuditStatusSchema = z.enum([
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
]);

export const PasswordAuditCrackMethodSchema = z.enum([
  'BREACHED_PASSWORD_WORST_100',
  'BREACHED_PASSWORD_WORST_1000',
  'BREACHED_PASSWORD_WORST_10000',
  'BREACH_TERM',
  'CREDENTIAL_STUFFING',
  'USERNAME_MUTATION',
  'CONTEXTUAL_TERM',
  'BREACH_LIST',
  'COMPANY_OSINT',
  'RULES',
  'UNKNOWN',
]);

export const InfraImpactCountSchema = z.object({
  DOMAIN_COMPROMISE: z.number(),
  HOST_COMPROMISE: z.number(),
  DOMAIN_USER_COMPROMISE: z.number(),
  SENSITIVE_DATA_EXPOSURE: z.number(),
  RANSOMWARE_EXPOSURE: z.number(),
  CREDENTIAL_EXPOSURE: z.number(),
  PRIVILEGE_ESCALATION: z.number(),
  LATERAL_MOVEMENT: z.number(),
});

export const BusinessRiskCountSchema = z.object({
  OPERATIONAL_DISRUPTION: z.number(),
  CRITICAL_SYSTEM_SHUTDOWN: z.number(),
  THEFT_OF_IP: z.number(),
  REGULATORY_BREACH: z.number(),
  FINANCIAL_LOSS: z.number(),
  REPUTATIONAL_DAMAGE: z.number(),
});

export const WeaknessCountRowSchema = z.object({
  category: z.string(),
  count: z.number(),
});

export const ThreatActorFlowRowSchema = z.object({
  actor: z.string(),
  category: z.string(),
  count: z.number(),
});

export const InternalNetworkHostSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  host: z.string(),
  status: z.string(),
  detectedAt: z.iso.datetime(),
  lostAt: z.iso.datetime().nullable(),
  ownedPrincipalEntityId: z.string().nullable(),
  isHvt: z.boolean().optional().default(false),
});

export const ListInternalNetworkHostsQuerySchema = z.object({
  firstObservedAfter: z.coerce.date().optional(),
  status: z.string().optional(),
  limit: z.coerce.number().min(1).max(500).optional(),
  skip: z.coerce.number().min(0).max(100_000).optional(),
});

export const ListInternalNetworkHostsResponseSchema = z.object({
  data: z.array(InternalNetworkHostSchema),
  total: z.number(),
});

export const StartPasswordAuditBodySchema = z.object({
  storeHashMaterial: z.boolean().optional().default(false),
});

export const PasswordAuditRiskLevelSchema = z.enum([
  'CRITICAL',
  'HIGH',
  'INFO',
]);

export const PasswordAuditResultSchema = z.object({
  id: z.string(),
  username: z.string(),
  cracked: z.boolean(),
  crackMethod: PasswordAuditCrackMethodSchema.nullable(),
  similarPasswordClusterId: z.string().nullable(),
  blastRadiusCount: z.number(),
  objectId: z.string().nullable().optional(),
  entityKind: z.literal('USER').nullable().optional(),
  score: z.number().optional(),
  riskLevel: PasswordAuditRiskLevelSchema.optional(),
  passwordLength: z.number().int().nullable().optional(),
  domain: z.string().nullable().optional(),
  pwdLastSet: z.iso.datetime().nullable().optional(),
  maskedHash: z.string().nullable().optional(),
  maskedPassword: z.string().nullable().optional(),
  createdAt: z.iso.datetime(),
});

export const PasswordAuditHistogramBucketSchema = z.object({
  key: z.string(),
  count: z.number(),
});

export const PasswordAuditSummarySchema = z.object({
  jobId: z.string(),
  hashCount: z.number(),
  crackedCount: z.number(),
  similarPasswordCount: z.number(),
  userCount: z.number(),
  crackedUserCount: z.number(),
  similarPasswordUserCount: z.number(),
  byRiskLevel: z.array(PasswordAuditHistogramBucketSchema),
  bySimilarPassword: z.array(PasswordAuditHistogramBucketSchema),
  byCrackMethod: z.array(PasswordAuditHistogramBucketSchema),
  byPasswordLength: z.array(PasswordAuditHistogramBucketSchema),
});

export const PasswordAuditJobSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  status: PasswordAuditStatusSchema,
  command: z.string(),
  tier: z.string(),
  storeHashMaterial: z.boolean(),
  hashCount: z.number(),
  crackedCount: z.number(),
  errorMessage: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  results: z.array(PasswordAuditResultSchema).optional(),
});

export const PasswordAuditJobListResponseSchema = z.object({
  data: z.array(PasswordAuditJobSchema),
  total: z.number(),
});

export const IngestPasswordAuditResultsBodySchema = z.object({
  results: z
    .array(
      z.object({
        username: z.string().min(1),
        cracked: z.boolean(),
        crackMethod: PasswordAuditCrackMethodSchema.optional(),
        similarPasswordClusterId: z.string().optional(),
        blastRadiusCount: z.number().int().min(0).optional(),
      }),
    )
    .min(1)
    .max(10_000),
});

export const UploadVriScanBodySchema = z.object({
  file: uploadedFileSchema({
    mimeTypes: ['text/csv', 'application/vnd.ms-excel', 'text/plain'],
    maxFileSizeBytes: 20 * 1024 * 1024,
  }),
  scannerSource: VriScannerSourceSchema.optional(),
});

export const VriClassificationSchema = z.object({
  id: z.string(),
  bucket: VriClassificationBucketSchema,
  cveId: z.string().nullable(),
  pluginId: z.string().nullable(),
  host: z.string().nullable(),
  assetName: z.string().nullable(),
  findingId: z.string().nullable(),
});

export const VriScanBatchSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  status: VriScanBatchStatusSchema,
  scannerSource: VriScannerSourceSchema,
  fileName: z.string(),
  rowCount: z.number(),
  expiresAt: z.iso.datetime(),
  errorMessage: z.string().nullable(),
  createdAt: z.iso.datetime(),
  classifications: z.array(VriClassificationSchema).optional(),
});

export const VriScanBatchListResponseSchema = z.object({
  data: z.array(VriScanBatchSchema),
  total: z.number(),
});

export const EMPTY_INFRA_IMPACT_COUNTS = {
  DOMAIN_COMPROMISE: 0,
  HOST_COMPROMISE: 0,
  DOMAIN_USER_COMPROMISE: 0,
  SENSITIVE_DATA_EXPOSURE: 0,
  RANSOMWARE_EXPOSURE: 0,
  CREDENTIAL_EXPOSURE: 0,
  PRIVILEGE_ESCALATION: 0,
  LATERAL_MOVEMENT: 0,
} as const satisfies z.infer<typeof InfraImpactCountSchema>;

export const EMPTY_BUSINESS_RISK_COUNTS = {
  OPERATIONAL_DISRUPTION: 0,
  CRITICAL_SYSTEM_SHUTDOWN: 0,
  THEFT_OF_IP: 0,
  REGULATORY_BREACH: 0,
  FINANCIAL_LOSS: 0,
  REPUTATIONAL_DAMAGE: 0,
} as const satisfies z.infer<typeof BusinessRiskCountSchema>;

export type InfraImpactCountDto = z.infer<typeof InfraImpactCountSchema>;
export type BusinessRiskCountDto = z.infer<typeof BusinessRiskCountSchema>;
export type InternalNetworkHostDto = z.infer<typeof InternalNetworkHostSchema>;
export type ListInternalNetworkHostsQueryDto = z.infer<
  typeof ListInternalNetworkHostsQuerySchema
>;
export type PasswordAuditJobDto = z.infer<typeof PasswordAuditJobSchema>;
export type PasswordAuditResultDto = z.infer<typeof PasswordAuditResultSchema>;
export type PasswordAuditSummaryDto = z.infer<
  typeof PasswordAuditSummarySchema
>;
export type VriScanBatchDto = z.infer<typeof VriScanBatchSchema>;
export type InfraImpactType = z.infer<typeof InfraImpactSchema>;
export type BusinessRiskType = z.infer<typeof BusinessRiskSchema>;
